import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import RefreshButton from './ui/RefreshButton';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

import { Badge } from './ui/badge';

import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

import { Coins, Package, Gift, Clock, Zap, Users, TrendingDown, Plus, RefreshCw, UserPlus, Cake, Award, FileText, Key, Calendar, Timer, ArrowUpRight, ArrowDownRight, BarChart3, Activity, Star } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import Sparkline from './credits/Sparkline';
import CreditUsagePlansTab from './CreditUsagePlansTab';
import CreditStatsAdvancedTab from './CreditStatsAdvancedTab';
import CreditAlertsAdminTab from './CreditAlertsAdminTab';
import GCPacksTab from './GestionCredits_PacksTab';
import GCBonusTab from './GestionCredits_BonusTab';
import GCUsersTab from './GestionCredits_UsersTab';
import GCTransactionsTab from './GestionCredits_TransactionsTab';
import GCPackModal from './GestionCredits_PackModal';
import GCBonusModal from './GestionCredits_BonusModal';
import GCAttributeModal from './GestionCredits_AttributeModal';
import GCEditModal from './GestionCredits_EditModal';
import GCDuplicateModal from './GestionCredits_DuplicateModal';
import GCBulkAttributeModal from './GestionCredits_BulkAttributeModal';
import { AVAILABLE_ICONS, COLORS, CountdownWidget, StatsCard } from './gestionCreditsShared';
import GCKpiCards from './GCKpiCards';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


// Composant principal
export default function GestionCredits({ getAuthHeader }) {
  const { t: t } = useTranslation();
  const [activeTab, setActiveTab] = useState('usage_plans');
  const [loading, setLoading] = useState(false);

  // États pour les données
  const [creditTypes, setCreditTypes] = useState([]);
  const [packs, setPacks] = useState([]);
  const [actions, setActions] = useState([]);
  const [bonusRules, setBonusRules] = useState([]);
  const [userBalances, setUserBalances] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [sparkSeries, setSparkSeries] = useState(null);

  // États pour les modales
  const [showPackModal, setShowPackModal] = useState(false);
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [showAttributeModal, setShowAttributeModal] = useState(false);
  const [editingPack, setEditingPack] = useState(null);
  const [editingBonus, setEditingBonus] = useState(null);
  const [attributeSearch, setAttributeSearch] = useState('');

  // États pour les formulaires
  const [packForm, setPackForm] = useState({
    nom: '',
    description: '',
    credits_inclus: 10,
    credits_bonus: 0,
    bonus_pourcentage: 0,
    prix_ht: 49,
    type_credit: 'STANDARD',
    icone: 'Package',
    couleur: '#3B82F6',
    populaire: false,
    ordre_affichage: 0
  });

  const [bonusForm, setBonusForm] = useState({
    code: '',
    nom: '',
    description: '',
    type_declencheur: 'EVENT',
    credits_bonus: 5,
    bonus_pourcentage: 0,
    type_credit: 'BONUS',
    duree_validite_jours: 30,
    conditions: {},
    icone: 'Gift'
  });

  const [attributeForm, setAttributeForm] = useState({
    user_id: '',
    credits: 10,
    type_credit: 'BONUS',
    raison: '',
    duree_validite_jours: null
  });

  const [searchTerm, setSearchTerm] = useState('');

  // Sélection multi-utilisateurs pour actions en masse
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({
    credits_standard: 0,
    credits_prioritaires: 0,
    credits_bonus: 0,
    credits_temporaires: 0,
    duree_validite_jours: null,
    raison: 'Modification manuelle des crédits',
  });
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateSource, setDuplicateSource] = useState(null);
  const [duplicateTargetSearch, setDuplicateTargetSearch] = useState('');
  const [duplicateTargetUserId, setDuplicateTargetUserId] = useState('');
  const [duplicateRaison, setDuplicateRaison] = useState('Duplication des crédits');
  const [showBulkAttributeModal, setShowBulkAttributeModal] = useState(false);
  const [bulkAttributeForm, setBulkAttributeForm] = useState({
    credits: 10,
    type_credit: 'BONUS',
    raison: '',
    duree_validite_jours: null,
  });

  // Chargement des données
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeader();
      const [typesRes, packsRes, actionsRes, rulesRes, balancesRes, transRes, statsRes, sparkRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/credits/types`, { headers }),
        axios.get(`${BACKEND_URL}/api/credits/packs`, { headers }),
        axios.get(`${BACKEND_URL}/api/credits/actions`, { headers }),
        axios.get(`${BACKEND_URL}/api/credits/bonus-rules`, { headers }),
        axios.get(`${BACKEND_URL}/api/credits/users/all-balances`, { headers }),
        axios.get(`${BACKEND_URL}/api/credits/transactions?limit=50`, { headers }),
        axios.get(`${BACKEND_URL}/api/credits/transactions/stats`, { headers }),
        axios.get(`${BACKEND_URL}/api/credits/transactions/sparkline?days=7`, { headers }).catch(() => ({ data: null }))
      ]);

      setCreditTypes(typesRes.data);
      setPacks(packsRes.data);
      setActions(actionsRes.data);
      setBonusRules(rulesRes.data);
      setUserBalances(balancesRes.data);
      // Trier les transactions du plus récent au plus ancien
      const sortedTransactions = (transRes.data || []).sort((a, b) => new Date(b.date_creation || b.created_at || 0) - new Date(a.date_creation || a.created_at || 0));
      setTransactions(sortedTransactions);
      setStats(statsRes.data);
      setSparkSeries(sparkRes.data);
    } catch {
      toast.error('Erreur lors du chargement des données');
    }
    setLoading(false);
  }, [getAuthHeader]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Gestion des packs
  const handleSavePack = async () => {
    try {
      const headers = getAuthHeader();
      if (editingPack) {
        await axios.put(`${BACKEND_URL}/api/credits/packs/${editingPack.id}`, packForm, { headers });
        toast.success('Pack mis à jour avec succès');
      } else {
        await axios.post(`${BACKEND_URL}/api/credits/packs`, packForm, { headers });
        toast.success('Pack créé avec succès');
      }
      setShowPackModal(false);
      setEditingPack(null);
      resetPackForm();
      loadData();
    } catch {
      toast.error('Erreur lors de la sauvegarde du pack');
    }
  };

  const handleDeletePack = async (packId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce pack ?')) return;
    try {
      const headers = getAuthHeader();
      await axios.delete(`${BACKEND_URL}/api/credits/packs/${packId}`, { headers });
      toast.success('Pack supprimé avec succès');
      loadData();
    } catch {
      toast.error('Erreur lors de la suppression du pack');
    }
  };

  const resetPackForm = () => {
    setPackForm({
      nom: '',
      description: '',
      credits_inclus: 10,
      credits_bonus: 0,
      bonus_pourcentage: 0,
      prix_ht: 49,
      type_credit: 'STANDARD',
      icone: 'Package',
      couleur: '#3B82F6',
      populaire: false,
      ordre_affichage: 0
    });
  };

  // Gestion des bonus
  const handleSaveBonus = async () => {
    try {
      const headers = getAuthHeader();
      if (editingBonus) {
        await axios.put(`${BACKEND_URL}/api/credits/bonus-rules/${editingBonus.id}`, bonusForm, { headers });
        toast.success('Règle de bonus mise à jour');
      } else {
        await axios.post(`${BACKEND_URL}/api/credits/bonus-rules`, bonusForm, { headers });
        toast.success('Règle de bonus créée');
      }
      setShowBonusModal(false);
      setEditingBonus(null);
      loadData();
    } catch {
      toast.error('Erreur lors de la sauvegarde de la règle');
    }
  };

  // Attribution manuelle de crédits
  const handleAttributeCredits = async () => {
    if (!attributeForm.user_id || !attributeForm.credits || !attributeForm.raison) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    try {
      const headers = getAuthHeader();
      await axios.post(`${BACKEND_URL}/api/credits/attribute`, attributeForm, { headers });
      toast.success(`${attributeForm.credits} crédits attribués avec succès`);
      setShowAttributeModal(false);
      setAttributeSearch('');
      setAttributeForm({ user_id: '', credits: 10, type_credit: 'BONUS', raison: '', duree_validite_jours: null });
      loadData();
    } catch {
      toast.error('Erreur lors de l\'attribution des crédits');
    }
  };

  // -------- Actions admin avancées sur les crédits --------
  const openEditModal = (userBalance) => {
    setEditTarget(userBalance);
    setEditForm({
      credits_standard: userBalance.credits_standard || 0,
      credits_prioritaires: userBalance.credits_prioritaires || 0,
      credits_bonus: userBalance.credits_bonus || 0,
      credits_temporaires: userBalance.credits_temporaires || 0,
      duree_validite_jours: null,
      raison: 'Modification manuelle des crédits',
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    if (!editForm.raison) {
      toast.error('La raison est obligatoire');
      return;
    }
    try {
      const headers = getAuthHeader();
      await axios.put(`${BACKEND_URL}/api/credits/users/${editTarget.user_id}/set`, editForm, { headers });
      toast.success('Crédits modifiés avec succès');
      setShowEditModal(false);
      setEditTarget(null);
      loadData();
    } catch {
      toast.error('Erreur lors de la modification des crédits');
    }
  };

  const handleDeleteUserCredits = async (userBalance) => {
    if (!window.confirm(`Supprimer (RAZ) TOUS les crédits de ${userBalance.user_info?.prenom || ''} ${userBalance.user_info?.nom || ''} ?`)) return;
    try {
      const headers = getAuthHeader();
      await axios.delete(`${BACKEND_URL}/api/credits/users/${userBalance.user_id}`, {
        headers,
        params: { raison: 'Suppression manuelle des crédits' },
      });
      toast.success('Crédits supprimés');
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const openDuplicateModal = (userBalance) => {
    setDuplicateSource(userBalance);
    setDuplicateTargetSearch('');
    setDuplicateTargetUserId('');
    setDuplicateRaison(`Duplication depuis ${userBalance.user_info?.email || userBalance.user_id}`);
    setShowDuplicateModal(true);
  };

  const handleConfirmDuplicate = async () => {
    if (!duplicateSource || !duplicateTargetUserId) {
      toast.error('Sélectionnez un utilisateur cible');
      return;
    }
    if (duplicateTargetUserId === duplicateSource.user_id) {
      toast.error('Source et cible identiques');
      return;
    }
    try {
      const headers = getAuthHeader();
      await axios.post(
        `${BACKEND_URL}/api/credits/users/${duplicateSource.user_id}/duplicate`,
        { target_user_id: duplicateTargetUserId, raison: duplicateRaison },
        { headers }
      );
      toast.success('Crédits dupliqués avec succès');
      setShowDuplicateModal(false);
      setDuplicateSource(null);
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors de la duplication');
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleSelectAllFiltered = () => {
    const allIds = filteredUsers.map((u) => u.user_id);
    const allSelected = allIds.every((id) => selectedUserIds.has(id));
    setSelectedUserIds(allSelected ? new Set() : new Set(allIds));
  };

  const handleBulkDelete = async () => {
    if (selectedUserIds.size === 0) return;
    if (!window.confirm(`Supprimer (RAZ) les crédits de ${selectedUserIds.size} utilisateur(s) ?`)) return;
    try {
      const headers = getAuthHeader();
      const res = await axios.post(
        `${BACKEND_URL}/api/credits/users/bulk-delete`,
        { user_ids: Array.from(selectedUserIds), raison: 'Suppression en masse' },
        { headers }
      );
      const ok = (res.data.results || []).filter((r) => r.success).length;
      toast.success(`${ok}/${res.data.count} suppression(s) effectuée(s)`);
      setSelectedUserIds(new Set());
      loadData();
    } catch {
      toast.error('Erreur lors de la suppression en masse');
    }
  };

  const handleBulkAttribute = async () => {
    if (!bulkAttributeForm.credits || !bulkAttributeForm.raison) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (selectedUserIds.size === 0) return;
    try {
      const headers = getAuthHeader();
      const res = await axios.post(
        `${BACKEND_URL}/api/credits/users/bulk-attribute`,
        { ...bulkAttributeForm, user_ids: Array.from(selectedUserIds) },
        { headers }
      );
      const ok = (res.data.results || []).filter((r) => r.success).length;
      toast.success(`${ok}/${res.data.count} attribution(s) effectuée(s)`);
      setShowBulkAttributeModal(false);
      setSelectedUserIds(new Set());
      setBulkAttributeForm({ credits: 10, type_credit: 'BONUS', raison: '', duree_validite_jours: null });
      loadData();
    } catch {
      toast.error('Erreur lors de l\'attribution en masse');
    }
  };

  // Export CSV (sélection ou utilisateurs filtrés)
  const exportUsersCsv = (rows, scopeLabel) => {
    if (!rows || rows.length === 0) {
      toast.error('Aucun utilisateur à exporter');
      return;
    }
    const header = ['Nom', 'Prenom', 'Email', 'Role', 'Standard', 'Prioritaire', 'Bonus', 'Temporaire', 'Expiration_temporaire', 'Total'];
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",;\n]/.test(s) ? `"${s}"` : s;
    };
    const lines = [header.join(';')];
    rows.forEach((u) => {
      const i = u.user_info || {};
      lines.push([
        escape(i.nom),
        escape(i.prenom),
        escape(i.email),
        escape(i.role || ''),
        u.credits_standard || 0,
        u.credits_prioritaires || 0,
        u.credits_bonus || 0,
        u.credits_temporaires || 0,
        escape(u.expiration_temporaires || ''),
        u.total_disponible || 0,
      ].join(';'));
    });
    // Préfixe BOM UTF-8 pour Excel
    const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const date = new Date().toISOString().slice(0, 10);
    const link = document.createElement('a');
    link.href = url;
    link.download = `credits_${scopeLabel}_${date}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`${rows.length} ligne(s) exportée(s) au format CSV`);
  };

  const handleExportSelection = () => {
    const rows = userBalances.filter((u) => selectedUserIds.has(u.user_id));
    exportUsersCsv(rows, 'selection');
  };

  const handleExportFiltered = () => {
    exportUsersCsv(filteredUsers, searchTerm ? `recherche_${searchTerm.replace(/\W+/g, '_')}` : 'tous');
  };

  // Rendu des icônes dynamiques
  const renderIcon = (iconName, className = "w-5 h-5") => {
    const iconMap = {
      Coins, Package, Gift, Clock, Zap, Users, Award, Star, Key, FileText, Calendar,
      UserPlus, Cake, RefreshCw
    };
    const IconComponent = iconMap[iconName] || Coins;
    return <IconComponent className={className} />;
  };

  // Filtrage des utilisateurs
  const filteredUsers = userBalances.filter(user => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const info = user.user_info || {};
    return (
      (info.nom || '').toLowerCase().includes(search) ||
      (info.prenom || '').toLowerCase().includes(search) ||
      (info.email || '').toLowerCase().includes(search)
    );
  });

  // ctx-bag : état + handlers partagés avec les sous-composants extraits
  const ctx = {
    actions, activeTab, attributeForm, attributeSearch, bonusForm, bonusRules,
    bulkAttributeForm, creditTypes, duplicateRaison, duplicateSource, duplicateTargetSearch,
    duplicateTargetUserId, editForm, editTarget, editingBonus, editingPack, loading,
    packForm, packs, searchTerm, selectedUserIds, showAttributeModal, showBonusModal,
    showBulkAttributeModal, showDuplicateModal, showEditModal, showPackModal, sparkSeries, stats,
    transactions, userBalances,
    setActions, setActiveTab, setAttributeForm, setAttributeSearch, setBonusForm, setBonusRules,
    setBulkAttributeForm, setCreditTypes, setDuplicateRaison, setDuplicateSource, setDuplicateTargetSearch,
    setDuplicateTargetUserId, setEditForm, setEditTarget, setEditingBonus, setEditingPack,
    setLoading, setPackForm, setPacks, setSearchTerm, setSelectedUserIds, setShowAttributeModal,
    setShowBonusModal, setShowBulkAttributeModal, setShowDuplicateModal, setShowEditModal, setShowPackModal,
    setSparkSeries, setStats, setTransactions, setUserBalances,
    exportUsersCsv, filteredUsers, handleAttributeCredits, handleBulkAttribute, handleBulkDelete,
    handleConfirmDuplicate, handleDeletePack, handleDeleteUserCredits, handleExportFiltered,
    handleExportSelection, handleSaveBonus, handleSaveEdit, handleSavePack, loadData, openDuplicateModal,
    openEditModal, renderIcon, resetPackForm, toggleSelectAllFiltered, toggleUserSelection,
    getAuthHeader, CountdownWidget, StatsCard, COLORS, AVAILABLE_ICONS,
  };

  return (
    <div className="space-y-6">
      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 shadow-xl">
        {/* Décor visuel */}
        <div className="absolute -right-12 -top-12 w-64 h-64 rounded-full bg-white/10 blur-3xl pointer-events-none" />
        <div className="absolute -left-20 -bottom-20 w-72 h-72 rounded-full bg-amber-300/20 blur-3xl pointer-events-none" />

        <div className="relative p-6 sm:p-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20 shadow-md">
                <Coins className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white">Système de Crédits</h2>
                <p className="text-amber-50/90 mt-1 text-sm">
                  Gérez les packs, bonus, attributions et consommation de crédits
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <RefreshButton
                onRefresh={loadData}
                loading={loading}
                successMessage="Données crédits actualisées"
                variant="light"
                testId="credits-actualiser-btn"
              />
              <Button
                onClick={() => { resetPackForm(); setEditingPack(null); setShowPackModal(true); }}
                className="bg-white text-orange-700 hover:bg-amber-50 shadow-md font-semibold"
                data-testid="credits-nouveau-pack-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouveau Pack
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Statistiques KPI - cartes gradient hero */}
      <GCKpiCards stats={stats} sparkSeries={sparkSeries} />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border border-slate-200 shadow-sm rounded-xl p-1.5 h-auto grid grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-1 w-full">
          <TabsTrigger value="usage_plans" className="flex items-center gap-2 px-3 py-2 rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-orange-500 data-[state=active]:text-white data-[state=active]:shadow-md transition-all text-sm font-medium" data-testid="credits-tab-usage-plans">
            <Zap className="w-4 h-4" />
            <span className="hidden sm:inline">Plans d&apos;usage</span>
          </TabsTrigger>
          <TabsTrigger value="packs" className="flex items-center gap-2 px-3 py-2 rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-orange-500 data-[state=active]:text-white data-[state=active]:shadow-md transition-all text-sm font-medium" data-testid="credits-tab-packs">
            <Package className="w-4 h-4" />
            <span className="hidden sm:inline">Packs</span>
          </TabsTrigger>
          <TabsTrigger value="bonus" className="flex items-center gap-2 px-3 py-2 rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-orange-500 data-[state=active]:text-white data-[state=active]:shadow-md transition-all text-sm font-medium" data-testid="credits-tab-bonus">
            <Gift className="w-4 h-4" />
            <span className="hidden sm:inline">Bonus</span>
          </TabsTrigger>
          <TabsTrigger value="alerts" className="flex items-center gap-2 px-3 py-2 rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-orange-500 data-[state=active]:text-white data-[state=active]:shadow-md transition-all text-sm font-medium" data-testid="credits-tab-alerts">
            <Activity className="w-4 h-4" />
            <span className="hidden sm:inline">Alertes</span>
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2 px-3 py-2 rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-orange-500 data-[state=active]:text-white data-[state=active]:shadow-md transition-all text-sm font-medium" data-testid="credits-tab-users">
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Attribution</span>
          </TabsTrigger>
          <TabsTrigger value="stats_advanced" className="flex items-center gap-2 px-3 py-2 rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-orange-500 data-[state=active]:text-white data-[state=active]:shadow-md transition-all text-sm font-medium" data-testid="credits-tab-stats-advanced">
            <BarChart3 className="w-4 h-4" />
            <span className="hidden sm:inline">Stats & Compta</span>
          </TabsTrigger>
          <TabsTrigger value="transactions" className="flex items-center gap-2 px-3 py-2 rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-orange-500 data-[state=active]:text-white data-[state=active]:shadow-md transition-all text-sm font-medium" data-testid="credits-tab-transactions">
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">Journal</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="usage_plans" className="space-y-4">
          <CreditUsagePlansTab getAuthHeader={getAuthHeader} />
        </TabsContent>

        <TabsContent value="alerts" className="space-y-4">
          <CreditAlertsAdminTab getAuthHeader={getAuthHeader} />
        </TabsContent>

        <TabsContent value="stats_advanced" className="space-y-4">
          <CreditStatsAdvancedTab getAuthHeader={getAuthHeader} />
        </TabsContent>

        {/* Tab: Packs */}
        <GCPacksTab ctx={ctx} />

        {/* Tab: Bonus */}
        <GCBonusTab ctx={ctx} />

        {/* Tab: Utilisateurs */}
        <GCUsersTab ctx={ctx} />

        {/* Tab: Transactions */}
        <GCTransactionsTab ctx={ctx} />
      </Tabs>

      {/* Modal: Créer/Modifier Pack */}
      <GCPackModal ctx={ctx} />

      {/* Modal: Créer/Modifier Bonus */}
      <GCBonusModal ctx={ctx} />

      {/* Modal: Attribuer des crédits */}
      <GCAttributeModal ctx={ctx} />

      {/* Modal: Modifier les crédits d'un utilisateur */}
      <GCEditModal ctx={ctx} />

      {/* Modal: Dupliquer les crédits vers un autre utilisateur */}
      <GCDuplicateModal ctx={ctx} />

      {/* Modal: Attribution en masse */}
      <GCBulkAttributeModal ctx={ctx} />
    </div>
  );
}
